import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { prettifyApiError } from "../../shared/api";
import { ChipFiltro } from "../../shared/ui/ChipFiltro";
import { ErrorBanner } from "../../shared/ui/ErrorBanner";
import { MarketplaceLogo } from "../../shared/ui/MarketplaceLogo";
import { PageHeader } from "../../shared/ui/PageHeader";
import {
  fetchSaldi,
  rileggiSaldi,
  type ContoSaldo,
  type LetturaSaldo,
  type Saldi,
} from "./saldiApi";
import { InBanca, VistaBanca, VistaControlli } from "./SaldiBanca";

/** Davide 28/10/2026: «una pagina che mostra il saldo all'interno dell'account
 *  di ogni marketplace». Regola: niente numeri inventati. Chi non da' il
 *  saldo a OM lo dice col motivo, chi e' giu' mostra l'ultimo saldo buono con
 *  la sua ora. Sola lettura: nessun tasto tocca un canale. */

const euro = (valore: string, valuta = "EUR") =>
  Number(valore).toLocaleString("it-IT", {
    style: "currency",
    currency: valuta,
    // «1.234,50 €» e non «1234,50 €»: l'italiano non raggruppa le 4 cifre.
    // "always" e' ES2023, i tipi della lib di TS qui non lo conoscono ancora.
    useGrouping: "always" as unknown as boolean,
  });

const giorno = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "short" });

const ora = (iso: string) =>
  new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

const quando = (iso: string) => {
  const d = new Date(iso);
  const oggi = new Date().toDateString() === d.toDateString();
  return oggi
    ? `alle ${ora(iso)}`
    : `il ${d.toLocaleDateString("it-IT", { day: "numeric", month: "short" })} alle ${ora(iso)}`;
};

type Esito = "letto" | "errore" | "calcolato" | "fuori" | "mai";

function esitoDi(conto: ContoSaldo): Esito {
  const stato = conto.ultima?.stato;
  if (stato === "ok") return "letto";
  if (stato === "errore") return "errore";
  if (stato === "non_disponibile" || stato === "da_collegare" || !conto.leggibile) {
    // Senza saldo vero, ma con quello rifatto da noi: si vede, segnato come tale.
    return conto.ultima?.calcolato != null ? "calcolato" : "fuori";
  }
  return "mai";
}

/** Il «calcolato da noi»: mai confondibile col saldo del canale. Bordo
 *  tratteggiato e la scritta sempre accanto al numero. */
function Calcolato({ lettura, conVero }: { lettura: LetturaSaldo; conVero: boolean }) {
  if (lettura.calcolato == null) return null;
  const differenza =
    conVero && lettura.saldo != null ? Number(lettura.saldo) - Number(lettura.calcolato) : null;
  return (
    <div className="mt-2 rounded-md border border-dashed border-line-2 px-3 py-2">
      <p className="flex flex-wrap items-baseline gap-x-2 text-[12.5px] text-ink-2">
        <span className="font-bold text-ink">Calcolato da noi</span>
        <span className="font-semibold tabular-nums text-ink">
          {euro(lettura.calcolato, lettura.valuta)}
        </span>
        {differenza != null && (
          <span className="tabular-nums">
            · il canale dice {differenza >= 0 ? "+" : ""}
            {euro(differenza.toFixed(2), lettura.valuta)}
          </span>
        )}
      </p>
      {lettura.calcolo_nota && (
        <p className="mt-0.5 text-[12px] leading-snug text-ink-2">{lettura.calcolo_nota}</p>
      )}
    </div>
  );
}

const ETICHETTA_STATO: Record<string, { testo: string; classe: string }> = {
  errore: {
    testo: "Non letto",
    classe: "border-red/40 bg-red-soft",
  },
  da_collegare: {
    testo: "Da collegare",
    classe: "border-amber/40 bg-amber-soft",
  },
  non_disponibile: {
    testo: "Senza API",
    classe: "border-line-2 bg-paper-2",
  },
};

function Etichetta({ stato }: { stato: string }) {
  const e = ETICHETTA_STATO[stato];
  if (!e) return null;
  return (
    <span
      className={`shrink-0 rounded-full border px-2 py-px text-[11px] font-bold text-ink ${e.classe}`}
    >
      {e.testo}
    </span>
  );
}

function Bonifico({
  titolo,
  importo,
  data,
  valuta,
}: {
  titolo: string;
  importo: string | null;
  data: string | null;
  valuta: string;
}) {
  if (importo === null) {
    return (
      <div className="max-lg:hidden">
        <p className="text-[11.5px] text-ink-2">{titolo}</p>
        <p className="text-[13px] text-ink-3">—</p>
      </div>
    );
  }
  return (
    <div>
      <p className="text-[11.5px] text-ink-2">{titolo}</p>
      <p className="text-[13px] font-semibold tabular-nums text-ink">
        {euro(importo, valuta)}
        {data && <span className="font-normal text-ink-2"> · {giorno(data)}</span>}
      </p>
    </div>
  );
}

function RigaConto({ conto }: { conto: ContoSaldo }) {
  const esito = esitoDi(conto);
  const ultima = conto.ultima;
  // Il numero da mostrare: quello di adesso, o l'ultimo buono se l'API e' giu'.
  const mostrata: LetturaSaldo | null =
    esito === "letto" ? ultima : esito === "errore" ? conto.ultimo_buono : null;
  const motivo =
    esito === "fuori" || esito === "calcolato" || esito === "errore" ? (ultima?.motivo ?? "") : "";
  // Senza saldo live, ma col bonifico che il canale ha gia' deciso (Brico Bravo:
  // il riepilogo del mese). Si vede, fuori dal totale.
  const soloBonifici =
    (esito === "fuori" || esito === "calcolato") &&
    ultima != null &&
    (ultima.in_arrivo != null || ultima.ultimo_bonifico != null);
  const bonifici: LetturaSaldo | null = mostrata ?? (soloBonifici ? ultima : null);

  return (
    <li className="px-4 py-3.5 transition-colors hover:bg-paper/50 max-md:px-3">
      <div className="grid grid-cols-[36px_minmax(0,1fr)_minmax(150px,auto)_170px_170px] items-center gap-x-5 max-lg:grid-cols-[36px_minmax(0,1fr)_auto] max-lg:gap-y-2">
        <MarketplaceLogo
          source={conto.logo}
          label={conto.nome}
          size={30}
          className="max-lg:self-start max-lg:pt-0.5"
        />

        <div className="min-w-0">
          <p className="flex items-center gap-2 text-[14px] font-bold text-ink">
            <span className="truncate">{conto.nome}</span>
            {ultima && ultima.stato !== "ok" && <Etichetta stato={ultima.stato} />}
            <InBanca stato={conto.in_banca} />
          </p>
          <p className="mt-0.5 text-[12px] leading-snug text-ink-2">
            {esito === "letto" && ultima && <>Letto {quando(ultima.letto_il)}</>}
            {esito === "errore" && (
              <>
                {motivo || "L'API non ha risposto"}
                {conto.ultimo_buono && (
                  <> · ultimo saldo letto {quando(conto.ultimo_buono.letto_il)}</>
                )}
              </>
            )}
            {(esito === "fuori" || esito === "calcolato") && motivo}
            {esito === "mai" && "Non ancora letto: il primo giro parte da solo entro un'ora."}
          </p>
          {esito === "letto" && ultima?.motivo && (
            <p className="mt-0.5 text-[12px] text-ink-2">{ultima.motivo}</p>
          )}
          {ultima && <Calcolato lettura={ultima} conVero={esito === "letto"} />}
        </div>

        {esito === "fuori" || esito === "calcolato" ? (
          <span className={soloBonifici ? "" : "lg:col-span-3"} />
        ) : (
          <p
            className={[
              "text-right font-display text-[20px] font-bold tabular-nums tracking-[-0.01em] max-md:text-[18px]",
              mostrata ? "text-ink" : "text-ink-3",
              esito === "errore" ? "opacity-60" : "",
            ].join(" ")}
            aria-label={
              mostrata?.saldo
                ? `Saldo ${euro(mostrata.saldo, mostrata.valuta)}`
                : "Saldo non disponibile"
            }
          >
            {mostrata?.saldo ? euro(mostrata.saldo, mostrata.valuta) : "—"}
          </p>
        )}

        {bonifici && (
          <div className="contents max-lg:col-start-2 max-lg:col-span-2 max-lg:flex max-lg:flex-wrap max-lg:gap-x-6 max-lg:gap-y-1.5">
            <Bonifico
              titolo="In arrivo in banca"
              importo={bonifici.in_arrivo}
              data={bonifici.in_arrivo_il}
              valuta={bonifici.valuta}
            />
            <Bonifico
              titolo="Ultimo bonifico"
              importo={bonifici.ultimo_bonifico}
              data={bonifici.ultimo_bonifico_il}
              valuta={bonifici.valuta}
            />
          </div>
        )}
      </div>
    </li>
  );
}

const ORDINE: Record<Esito, number> = { letto: 0, errore: 1, calcolato: 2, mai: 3, fuori: 4 };

function Gruppo({ titolo, conti: tutti }: { titolo: string; conti: ContoSaldo[] }) {
  if (tutti.length === 0) return null;
  // Prima i conti col numero, in fondo quelli che OM non puo' leggere.
  const conti = [...tutti].sort((a, b) => ORDINE[esitoDi(a)] - ORDINE[esitoDi(b)]);
  const id = `gruppo-${titolo.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <section aria-labelledby={id} className="mb-7 last:mb-0">
      <h2 id={id} className="mb-2 px-1 font-display text-[18px] font-bold text-ink">
        {titolo}
      </h2>
      <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-card">
        {conti.map((c) => (
          <RigaConto key={c.chiave} conto={c} />
        ))}
      </ul>
    </section>
  );
}

function Scheletro() {
  return (
    <div aria-hidden="true" className="rounded-lg border border-line bg-card">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className="flex items-center gap-4 border-b border-line px-4 py-4 last:border-b-0"
        >
          <div className="h-[30px] w-[30px] rounded-full bg-paper-2" />
          <div className="h-3.5 w-40 animate-pulse rounded bg-paper-2" />
          <div className="ml-auto h-5 w-24 animate-pulse rounded bg-paper-2" />
        </div>
      ))}
    </div>
  );
}

/** Le tre viste della pagina (Davide 02/11/2026): i saldi dei canali, il conto
 *  in banca dall'estratto caricato, i controlli sui soldi. Nell'URL, cosi' un
 *  link porta dritto ai controlli. */
type Vista = "canali" | "banca" | "controlli";
const VISTE: { chiave: Vista; nome: string }[] = [
  { chiave: "canali", nome: "Canali" },
  { chiave: "banca", nome: "Banca" },
  { chiave: "controlli", nome: "Controlli" },
];

function Viste({
  vista,
  onVista,
  aperti,
}: {
  vista: Vista;
  onVista: (v: Vista) => void;
  aperti: number;
}) {
  return (
    <div
      role="tablist"
      aria-label="Viste dei saldi"
      className="mb-5 flex flex-wrap gap-1.5 max-md:gap-2"
    >
      {VISTE.map((v) => (
        <ChipFiltro
          key={v.chiave}
          role="tab"
          tono={v.chiave === "controlli" && aperti > 0 ? "da-pagare" : "spedito"}
          attivo={vista === v.chiave}
          onClick={() => onVista(v.chiave)}
          conteggio={v.chiave === "controlli" && aperti > 0 ? aperti : undefined}
          evidenzia={v.chiave === "controlli" && aperti > 0}
          title={
            v.chiave === "controlli" && aperti > 0
              ? `${aperti} ${aperti === 1 ? "controllo da guardare" : "controlli da guardare"}`
              : undefined
          }
        >
          {v.nome}
        </ChipFiltro>
      ))}
    </div>
  );
}

export function SaldiPage() {
  const [params, setParams] = useSearchParams();
  const vista: Vista = VISTE.some((v) => v.chiave === params.get("vista"))
    ? (params.get("vista") as Vista)
    : "canali";
  const scegliVista = (v: Vista) =>
    setParams(v === "canali" ? {} : { vista: v }, { replace: true });
  const [dati, setDati] = useState<Saldi | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [rileggo, setRileggo] = useState<"no" | "in_corso" | "accodato" | "gia">("no");

  const ricarica = useCallback(async (signal?: AbortSignal) => {
    try {
      setDati(await fetchSaldi(signal));
      setErrore(null);
    } catch (err) {
      if (signal?.aborted) return;
      setErrore(prettifyApiError(err));
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void ricarica(ctrl.signal);
    return () => ctrl.abort();
  }, [ricarica]);

  const conti = useMemo(() => dati?.conti ?? [], [dati]);
  const fuori = conti.filter((c) => ["fuori", "mai", "calcolato"].includes(esitoDi(c)));
  const nonLetti = conti.filter((c) => esitoDi(c) === "errore");
  const ultimaLettura = conti
    .map((c) => c.ultima?.letto_il)
    .filter((v): v is string => Boolean(v))
    .sort()
    .at(-1);

  async function rileggi() {
    setRileggo("in_corso");
    try {
      const esito = await rileggiSaldi();
      setRileggo(esito.accodato ? "accodato" : "gia");
    } catch (err) {
      setRileggo("no");
      setErrore(prettifyApiError(err));
    }
  }

  return (
    <main className="flex-1 overflow-y-auto px-7 pt-[22px] pb-8 scroll-paper max-md:px-3 max-md:pt-3">
      <PageHeader
        title="Saldi"
        subtitle="Quanto c'è adesso sul conto di ogni marketplace e dei pagamenti di Kery, letto dai canali ogni ora, e cosa è arrivato davvero in banca. Dove il canale non lo dà, lo calcoliamo noi e lo scriviamo."
        meta={
          <Link to="/fatturazione" className="font-semibold text-ink hover:underline max-md:hidden">
            ← Fatturazione
          </Link>
        }
      />

      <ErrorBanner message={errore} className="mb-3" />

      {dati === null && !errore && <Scheletro />}

      {dati !== null && (
        <Viste
          vista={vista}
          onVista={scegliVista}
          aperti={dati.controlli.filter((c) => c.stato === "da_controllare").length}
        />
      )}

      {dati !== null && vista === "banca" && (
        <VistaBanca banca={dati.banca} conti={conti} ricarica={() => ricarica()} />
      )}

      {dati !== null && vista === "controlli" && (
        <VistaControlli controlli={dati.controlli} conBanca={dati.banca !== null} />
      )}

      {dati !== null && vista === "canali" && (
        <>
          <div className="mb-7 flex flex-wrap items-end justify-between gap-x-8 gap-y-3 rounded-lg border border-line bg-card px-5 py-4 max-md:px-4">
            <div>
              <p className="text-[13px] text-ink-2">
                Sui {dati.letti === 1 ? "conto letto" : `${dati.letti} conti letti`}
              </p>
              <p className="font-display text-[34px] font-bold leading-[1.05] tabular-nums tracking-[-0.02em] text-ink max-md:text-[30px]">
                {euro(dati.totale)}
              </p>
              <p className="mt-1 max-w-[62ch] text-[12.5px] leading-snug text-ink-2">
                {fuori.length + nonLetti.length === 0
                  ? "Tutti i conti sono letti."
                  : `Fuori dal totale: ${[...nonLetti, ...fuori].map((c) => c.nome).join(", ")}. Il motivo è scritto sotto, conto per conto.`}
              </p>
              {dati.calcolati > 0 && (
                <p className="mt-1 text-[12.5px] text-ink-2">
                  Calcolato da noi, dove il canale non dà il saldo:{" "}
                  <span className="font-semibold tabular-nums text-ink">
                    {euro(dati.totale_calcolato)}
                  </span>{" "}
                  (non è nel totale)
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1.5 max-md:w-full max-md:items-stretch">
              <button
                type="button"
                onClick={() => void rileggi()}
                disabled={rileggo === "in_corso" || dati.aggiornamento_in_corso}
                title="Legge subito i saldi invece di aspettare il giro di ogni ora"
                className="inline-flex h-[34px] items-center justify-center gap-1.5 rounded-full border border-line-2 bg-card px-3.5 text-[13px] font-semibold text-ink transition-colors hover:border-ink-3 hover:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-4 disabled:cursor-not-allowed disabled:opacity-50 max-md:h-11 max-md:text-[14px]"
              >
                <svg
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  className={`h-[14px] w-[14px] fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round] ${rileggo === "in_corso" || dati.aggiornamento_in_corso ? "animate-spin" : ""}`}
                >
                  <path d="M20 11a8 8 0 1 0-2.3 5.7M20 4v7h-7" />
                </svg>
                {dati.aggiornamento_in_corso ? "Lettura in corso" : "Aggiorna ora"}
              </button>
              <p className="text-[12px] text-ink-2 max-md:text-center">
                {ultimaLettura ? `Ultima lettura ${quando(ultimaLettura)}` : "Mai letti"}
              </p>
            </div>
          </div>

          {rileggo === "accodato" && (
            <p role="status" className="-mt-4 mb-4 text-[12.5px] text-ink-2">
              Lettura avviata: fra un minuto ricarica la pagina.
            </p>
          )}
          {rileggo === "gia" && (
            <p role="status" className="-mt-4 mb-4 text-[12.5px] text-ink-2">
              Una lettura è già in corso.
            </p>
          )}

          <Gruppo titolo="Marketplace" conti={conti.filter((c) => c.gruppo === "marketplace")} />
          <Gruppo
            titolo="Pagamenti di Kery"
            conti={conti.filter((c) => c.gruppo === "pagamenti")}
          />
        </>
      )}
    </main>
  );
}
